import { HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  Accuracy,
  AreaThePriceRefersTo,
  Category,
  Listing,
  OfferType,
  PriceInterval,
} from '@smg-real-estate/hg-rets-schema';
import { cloneDeep } from 'lodash-es';
import type { ComposerTranslation } from 'vue-i18n';
import { type RouteLocationNormalizedLoadedGeneric } from 'vue-router';

import { localizedLandingPagesPaths } from '@/router/routes/landingPageRoutes';

export function getTranslatedListingCategories(
  t: ComposerTranslation,
  categories: Category[] | undefined,
): string[] {
  if (!categories || categories.length === 0) {
    return [t(`listing.categories.${Category.Apartment}`).toString()];
  }

  const filtered = new Set<string>();
  categories.forEach((category) => {
    filtered.add(t(`listing.categories.${category}`).toString());
  });

  return Array.from(filtered);
}

export enum TopCategoryGroup {
  RESIDENTIAL = 'RESIDENTIAL',
  COMMERCIAL = 'COMMERCIAL',
  PARKING = 'PARKING',
}

// A collection of similar Category's
export enum TopCategory {
  AGRI = 'AGRI',
  APPT = 'APPT',
  GASTRO = 'GASTRO',
  HOUSE = 'HOUSE',
  INDUS = 'INDUS',
  PARK = 'PARK',
  PROP = 'PROP',
  SECONDARY = 'SECONDARY',
  FURNISHED = 'FURNISHED',
  SHARED_APARTMENT = 'SHARED_APARTMENT',
}

export const topCategoryGroups: Record<TopCategoryGroup, TopCategory[]> = {
  [TopCategoryGroup.RESIDENTIAL]: [
    TopCategory.APPT,
    TopCategory.HOUSE,
    TopCategory.PROP,
    TopCategory.SECONDARY,
    TopCategory.FURNISHED,
    TopCategory.SHARED_APARTMENT,
  ],
  [TopCategoryGroup.COMMERCIAL]: [TopCategory.GASTRO, TopCategory.AGRI, TopCategory.INDUS],
  [TopCategoryGroup.PARKING]: [TopCategory.PARK],
};

export const topCategories: Record<TopCategory, Category[]> = {
  [TopCategory.APPT]: [
    Category.Apartment,
    Category.Duplex,
    Category.AtticFlat,
    Category.RoofFlat,
    Category.Studio,
    Category.SingleRoom,
    Category.TerraceFlat,
    Category.BachelorFlat,
    Category.Loft,
    Category.Attic,
  ],
  [TopCategory.HOUSE]: [
    Category.SingleHouse,
    Category.RowHouse,
    Category.BifamiliarHouse,
    Category.TerraceHouse,
    Category.Villa,
    Category.FarmHouse,
    Category.MultipleDwelling,
    Category.Castle,
    Category.GrannyFlat,
    Category.Chalet,
    Category.RusticHouse,
  ],
  [TopCategory.PROP]: [Category.BuildingLand, Category.CommercialLand],
  [TopCategory.SECONDARY]: [Category.HobbyRoom, Category.CellarCompartment],
  [TopCategory.FURNISHED]: [Category.FurnishedFlat],
  [TopCategory.SHARED_APARTMENT]: [Category.SharedApartment],
  [TopCategory.GASTRO]: [
    Category.Hotel,
    Category.Restaurant,
    Category.Coffeehouse,
    Category.Bar,
    Category.ClubDisco,
    Category.Pub,
  ],
  [TopCategory.AGRI]: [Category.AgriculturalInstallation],
  [TopCategory.INDUS]: [
    Category.Office,
    Category.Shop,
    Category.Commercial,
    Category.StorageRoom,
    Category.Practice,
    Category.Kiosk,
    Category.Garage,
    Category.Hairdresser,
    Category.ShoppingCentre,
    Category.Factory,
    Category.IndustrialObject,
    Category.Arcade,
    Category.Atelier,
    Category.ResidentialCommercialBuilding,
    Category.Workshop,
    Category.DepartmentStore,
    Category.ParkingSpace,
  ],
  [TopCategory.PARK]: [
    Category.OpenSlot,
    Category.CoveredSlot,
    Category.SingleGarage,
    Category.DoubleGarage,
    Category.UndergroundSlot,
    Category.CoveredParkingPlaceBike,
  ],
};

export const topCategoriesOrdered: Record<TopCategory, Category[]> = {
  [TopCategory.APPT]: [
    Category.Apartment,
    Category.RoofFlat,
    Category.AtticFlat,
    Category.Duplex,
    Category.TerraceFlat,
    Category.Studio,
    Category.SingleRoom,
    Category.BachelorFlat,
    Category.Loft,
  ],
  [TopCategory.HOUSE]: [
    Category.SingleHouse,
    Category.RowHouse,
    Category.BifamiliarHouse,
    Category.MultipleDwelling,
    Category.Villa,
    Category.FarmHouse,
    Category.Chalet,
    Category.RusticHouse,
    Category.TerraceHouse,
    Category.Castle,
  ],
  [TopCategory.PROP]: [Category.BuildingLand, Category.CommercialLand],
  [TopCategory.SECONDARY]: [
    Category.HobbyRoom,
    Category.AtticCompartment,
    Category.CellarCompartment,
  ],
  [TopCategory.FURNISHED]: [Category.FurnishedFlat],
  [TopCategory.SHARED_APARTMENT]: [Category.SharedApartment],
  [TopCategory.GASTRO]: [
    Category.Restaurant,
    Category.Bar,
    Category.Hotel,
    Category.Coffeehouse,
    Category.ClubDisco,
  ],
  [TopCategory.AGRI]: [Category.AgriculturalInstallation],
  [TopCategory.INDUS]: [
    Category.Office,
    Category.Shop,
    Category.Commercial,
    Category.StorageRoom,
    Category.Practice,
    Category.IndustrialObject,
    Category.Atelier,
    Category.Workshop,
    Category.Hairdresser,
    Category.ResidentialCommercialBuilding,
    Category.Arcade,
    Category.DepartmentStore,
    Category.Kiosk,
    Category.Factory,
    Category.ShoppingCentre,
  ],
  [TopCategory.PARK]: [
    Category.UndergroundSlot,
    Category.OpenSlot,
    Category.SingleGarage,
    Category.CoveredSlot,
  ],
};

export const enrichPreviewListing = (listing: Listing) => {
  const listingPreview: Listing = cloneDeep(listing);
  listingPreview.id = '-';
  listingPreview.externalIds = {
    ...listingPreview.externalIds,
    displayPropertyReferenceId: getObjectReference(listingPreview),
  };

  if (listingPreview.address) {
    listingPreview.address.geoCoordinates = {
      ...listingPreview.address.geoCoordinates,
      accuracy: Accuracy.High,
    };
  }

  return listingPreview;
};

export function buildListingObjectTypeKey(
  topCategory: TopCategory,
  listingType: Category | string,
): string {
  return `${topCategory}_${listingType}`.toUpperCase();
}

export function mapListingCategoriesFromSelection(
  topCategory: TopCategory,
  listingType: Category | string,
): Category[] | undefined {
  return objectTypeMappings[buildListingObjectTypeKey(topCategory, listingType)];
}

function matchesListingCategorySelection(
  categories: Category[],
  topCategory: TopCategory,
  listingType: Category,
): boolean {
  const mappedCategories = mapListingCategoriesFromSelection(topCategory, listingType);

  return (
    !!mappedCategories &&
    mappedCategories.length === categories.length &&
    mappedCategories.every((category, index) => category === categories[index])
  );
}

function findListingCategorySelectionForTopCategory(
  categories: Category[],
  topCategory: TopCategory,
): { topCategory: TopCategory; listingType: Category } | undefined {
  const listingTypes = topCategoriesOrdered[topCategory] ?? [];

  for (const listingType of listingTypes) {
    if (matchesListingCategorySelection(categories, topCategory, listingType)) {
      return { topCategory, listingType };
    }
  }

  return undefined;
}

export function resolveListingCategorySelection(categories: Category[] | undefined):
  | {
      topCategory: TopCategory;
      listingType: Category;
    }
  | undefined {
  if (!categories?.length) return undefined;

  for (const topCategory of Object.values(TopCategory)) {
    const match = findListingCategorySelectionForTopCategory(categories, topCategory);
    if (match) return match;
  }

  return undefined;
}

export const objectTypeMappings: { [index: string]: Category[] } = {
  AGRI_AGRICULTURAL_INSTALLATION: [Category.Farm, Category.AgriculturalInstallation],
  AGRI_MOUNTAIN_FARM: [Category.Farm, Category.MountainFarm],
  AGRI_FARM: [Category.Farm],
  APPT_APARTMENT: [Category.Apartment, Category.Flat],
  APPT_DUPLEX: [Category.Apartment, Category.Duplex],
  APPT_ATTIC_FLAT: [Category.Apartment, Category.AtticFlat],
  APPT_ROOF_FLAT: [Category.Apartment, Category.RoofFlat],
  APPT_STUDIO: [Category.Apartment, Category.Studio],
  APPT_SINGLE_ROOM: [Category.Apartment, Category.SingleRoom],
  APPT_FURNISHED_DWELLING: [Category.Apartment, Category.FurnishedFlat],
  APPT_FURNISHED_FLAT: [Category.Apartment, Category.FurnishedFlat],
  APPT_TERRACE_FLAT: [Category.Apartment, Category.TerraceFlat],
  APPT_BACHELOR_FLAT: [Category.Apartment, Category.BachelorFlat],
  APPT_LOFT: [Category.Apartment, Category.Loft],
  APPT_ATTIC: [Category.Apartment, Category.Attic],
  APPT_FLATSHARE: [Category.Apartment, Category.SharedApartment],
  GARDEN_ALLOTTMENT_GARDEN: [Category.AlottmentGarden],
  GASTRO_HOTEL: [Category.Hotel],
  GASTRO_RESTAURANT: [Category.CafeBar, Category.Restaurant],
  GASTRO_COFFEEHOUSE: [Category.CafeBar, Category.Coffeehouse],
  GASTRO_BAR: [Category.CafeBar, Category.Bar],
  GASTRO_CLUB_DISCO: [Category.CafeBar, Category.ClubDisco],
  GASTRO_CASINO: [Category.CafeBar, Category.Casino],
  GASTRO_MOVIE_THEATER: [Category.CafeBar, Category.MovieTheater],
  GASTRO_SQUASH_BADMINTON: [Category.SquashBadminton],
  GASTRO_INDOOR_TENNIS_COURTS: [Category.IndoorTennisCourt],
  GASTRO_TENNIS_COURT: [Category.TennisCourt],
  GASTRO_SPORTS_HALL: [Category.SportsHall],
  GASTRO_CAMPGROUND_TENT_CAMPING: [Category.Campground],
  GASTRO_OUTDOOR_SWIMMING_POOL: [Category.OutdoorSwimmingPool],
  GASTRO_INDOOR_SWIMMINGPOOL: [Category.IndoorSwimmingPool],
  GASTRO_GOLF_COURSE: [Category.GolfCourse],
  GASTRO_MOTEL: [Category.Motel, Category.BedAndBreakfast],
  GASTRO_PUB: [Category.CafeBar, Category.Pub],
  HOUSE_SINGLE_HOUSE: [Category.House, Category.SingleHouse],
  HOUSE_ROW_HOUSE: [Category.House, Category.RowHouse],
  HOUSE_BIFAMILIAR_HOUSE: [Category.House, Category.BifamiliarHouse],
  HOUSE_TERRACE_HOUSE: [Category.House, Category.TerraceHouse],
  HOUSE_VILLA: [Category.House, Category.Villa],
  HOUSE_FARM_HOUSE: [Category.House, Category.FarmHouse],
  HOUSE_MULTIPLE_DWELLING: [Category.House, Category.MultipleDwelling],
  HOUSE_CAVE_HOUSE_EARTHEN_HOUSE: [Category.House, Category.CaveHouse],
  HOUSE_CASTLE: [Category.House, Category.Castle],
  HOUSE_GRANNY_FLAT: [Category.Flat, Category.GrannyFlat],
  HOUSE_CHALET: [Category.House, Category.Chalet],
  HOUSE_RUSTIC_HOUSE: [Category.House, Category.RusticHouse],
  INDUS_OFFICE: [Category.Office],
  INDUS_SHOP: [Category.Retail, Category.Shop],
  INDUS_ADVERTISING_AREA: [Category.Retail, Category.AdvertisingArea],
  INDUS_COMMERCIAL: [Category.Retail, Category.Commercial],
  INDUS_STORAGE_ROOM: [Category.Retail, Category.StorageRoom],
  INDUS_PRACTICE: [Category.Retail, Category.Practice],
  INDUS_KIOSK: [Category.Retail, Category.Kiosk],
  INDUS_GARDENING: [Category.Retail, Category.Gardening],
  INDUS_FUEL_STATION: [Category.Retail, Category.FuelStation],
  INDUS_GARAGE: [Category.Retail, Category.Garage],
  INDUS_CHEESE_FACTORY: [Category.Retail, Category.CheeseFactory],
  INDUS_BUTCHER: [Category.Retail, Category.Butcher],
  INDUS_BAKERY: [Category.Retail, Category.Bakery],
  INDUS_HAIRDRESSER: [Category.Retail, Category.Hairdresser],
  INDUS_SHOPPING_CENTRE: [Category.Retail, Category.ShoppingCentre],
  INDUS_FACTORY: [Category.Factory],
  INDUS_INDUSTRIAL_OBJECT: [Category.Factory, Category.IndustrialObject],
  INDUS_ARCADE: [Category.RetailSpace, Category.Arcade],
  INDUS_ATELIER: [Category.RetailSpace, Category.Atelier],
  INDUS_LIVING_COMMERCIAL_BUILDING: [Category.ResidentialCommercialBuilding],
  INDUS_RESIDENTIAL_COMMERCIAL_BUILDING: [Category.ResidentialCommercialBuilding],
  INDUS_LIBRARY: [Category.RetailSpace, Category.Library],
  INDUS_HOSPITAL: [Category.Hospital],
  INDUS_LABORATORY: [Category.Office, Category.Laboratory],
  INDUS_MINI_GOLF_COURSE: [Category.MiniGolfCourse],
  INDUS_NURSING_HOME: [Category.NursingHome],
  INDUS_RIDING_HALL: [Category.RidingHall],
  INDUS_SANATORIUM: [Category.Sanatorium],
  INDUS_WORKSHOP: [Category.Workshop],
  INDUS_PARTY_ROOM: [Category.CafeBar, Category.PartyRoom],
  INDUS_SAUNA: [Category.Sauna],
  INDUS_SOLARIUM: [Category.Solarium],
  INDUS_CARPENTRY_SHOP: [Category.Factory, Category.CarpentryShop],
  INDUS_OLD_AGE_HOME: [Category.OldAgeHome],
  INDUS_DEPARTMENT_STORE: [Category.Retail, Category.DepartmentStore],
  INDUS_HOME: [Category.Institution],
  INDUS_DISPLAY_WINDOW: [Category.DisplayWindow],
  INDUS_PARKING_GARAGE: [Category.ParkingSpace],
  INDUS_PARKING_SPACE: [Category.ParkingSpace],
  INDUS_PARKING_SURFACE: [Category.CarPark],
  PARK_OPEN_SLOT: [Category.OpenSlot],
  PARK_COVERED_SLOT: [Category.CoveredSlot],
  PARK_SINGLE_GARAGE: [Category.SingleGarage],
  PARK_DOUBLE_GARAGE: [Category.DoubleGarage],
  PARK_UNDERGROUND_SLOT: [Category.UndergroundSlot],
  PARK_BOAT_DRY_DOCK: [Category.BoatDryDock],
  PARK_BOAT_LANDING_STAGE: [Category.BoatLandingStage],
  PARK_COVERED_PARKING_PLACE_BIKE: [Category.Garage, Category.CoveredParkingPlaceBike],
  PARK_OUTDOOR_PARKING_PLACE_BIKE: [Category.Garage, Category.OutdoorParkingPlaceBike],
  PARK_HORSE_BOX: [Category.HorseBox],
  PARK_BOAT_MOORING: [Category.BoatMooring],
  PROP_BUILDING_LAND: [Category.Plot, Category.BuildingLand],
  PROP_AGRICULTURAL_LAND: [Category.AgriculturalLand],
  PROP_COMMERCIAL_LAND: [Category.Plot, Category.CommercialLand],
  PROP_INDUSTRIAL_LAND: [Category.Plot, Category.IndustrialLand],
  SECONDARY_HOBBY_ROOM: [Category.HobbyRoom],
  SECONDARY_CELLAR_COMPARTMENT: [Category.CellarCompartment],
  SECONDARY_ATTIC_COMPARTMENT: [Category.AtticCompartment],
  FURNISHED_FURNISHED_FLAT: [Category.FurnishedFlat],
  SHARED_APARTMENT_SHARED_APARTMENT: [Category.SharedApartment],
};

// Mapping of the fields and corresponding categories. Category.Garage and Category.ParkingSpace belong to indus categories,
// but refers to parking categories, so those are filtered out in certain usecases
const detailsFieldVisibilityMapping: { [index: string]: Category[] } = {
  ROOMS: [
    ...topCategories['APPT'],
    ...topCategories['FURNISHED'],
    ...topCategories['SHARED_APARTMENT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
  ],
  LIVING_SPACE: [
    ...topCategories['APPT'],
    ...topCategories['FURNISHED'],
    ...topCategories['SHARED_APARTMENT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
  ],
  FLOOR: [
    ...topCategories['APPT'],
    ...topCategories['FURNISHED'],
    ...topCategories['SHARED_APARTMENT'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
  ],
  NUMBER_OF_FLOORS: [
    ...topCategories['HOUSE'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
    Category.Duplex,
  ],
  LOT_SIZE: [
    ...topCategories['HOUSE'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
    ...topCategories['PROP'],
  ],
  FLOOR_SPACE: [
    ...topCategories['HOUSE'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
    ...topCategories['PARK'],
  ],
  ROOM_HEIGHT: [
    ...topCategories['HOUSE'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
    Category.UndergroundSlot,
  ],
  VOLUME: [...topCategories['HOUSE']],
  YEAR_BUILT: [
    ...topCategories['APPT'],
    ...topCategories['FURNISHED'],
    ...topCategories['SHARED_APARTMENT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
    ...topCategories['PARK'],
  ],
  LAST_RENOVATED: [
    ...topCategories['APPT'],
    ...topCategories['FURNISHED'],
    ...topCategories['SHARED_APARTMENT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
  ],
  VIDEO: [
    ...topCategories['APPT'],
    ...topCategories['FURNISHED'],
    ...topCategories['SHARED_APARTMENT'],
    ...topCategories['HOUSE'],
  ].filter((item) => ![Category.Garage, Category.ParkingSpace].includes(item)),
};

export const filterFields = (categories: Category[]): string[] => {
  return Object.keys(detailsFieldVisibilityMapping).filter((field) =>
    detailsFieldVisibilityMapping[field].some((category: any) => categories.includes(category)),
  );
};

export const isCrosslistingBundle = (bundleOrBundleId?: string): boolean => {
  if (!bundleOrBundleId) return false;
  return bundleOrBundleId.includes('CROSSLISTING');
};

export function isCommercialObject(listingCategories: Category[]): boolean {
  const commercialCategories = [
    Category.Office,
    Category.Retail,
    Category.Factory,
    Category.RetailSpace,
    Category.ResidentialCommercialBuilding,
    Category.Hospital,
    Category.MiniGolfCourse,
    Category.NursingHome,
    Category.RidingHall,
    Category.Sanatorium,
    Category.Workshop,
    Category.PartyRoom,
    Category.Sauna,
    Category.Solarium,
    Category.OldAgeHome,
    Category.Institution,
    Category.DisplayWindow,
    Category.ParkingSpace,
    Category.CarPark,
  ];
  return !!listingCategories?.some((category) => commercialCategories.includes(category));
}

export function applyCategoryDependentRentDefaults(listing: Listing, categories: Category[]): void {
  if (listing.offerType !== OfferType.Rent) {
    return;
  }

  const existingRent = listing.prices?.rent ?? {};
  const isCommercial = isCommercialObject(categories);

  listing.prices = {
    ...listing.prices,
    rent: {
      ...existingRent,
      interval: existingRent.interval ?? PriceInterval.Month,
      area: isCommercial ? (existingRent.area ?? AreaThePriceRefersTo.All) : undefined,
    },
  };
}

export const getLanguageSwitchLink = (
  route: RouteLocationNormalizedLoadedGeneric,
  language: HgSupportedLanguage,
) => {
  const routeName = route.path.includes(
    localizedLandingPagesPaths[route.meta?.locale as HgSupportedLanguage],
  )
    ? `landingpage-${language}`
    : route.name;

  return {
    name: routeName,
    query: route.query,
    params: {
      ...route.params,
      locale: language,
    },
  };
};

function getObjectReference(listingPreview: Listing) {
  return listingPreview.externalIds?.internalReferenceId
    ?.split('#')
    .reverse()
    .slice(0, 3)
    .filter((value: string) => value.length > 0)
    .join('.');
}
